import { Authorization, Token, UserInfo } from '@/constants/authorization';
import { getSearchValue } from './common-util';
const KeySet = [Authorization, Token, UserInfo];
const USER_STATUS_CACHE_COOKIE = 'ragflow_user_status_cache';
const USER_STATUS_INVALIDATED_EVENT = 'ragflow:user-status-invalidated';
const AUTH_BOOTSTRAP_PENDING_KEY = 'ragflow_auth_bootstrap_pending';

const setCookie = (name: string, value: string, days = 1) => {
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

const getCookie = (name: string): string => {
  const prefix = `${name}=`;
  const row = document.cookie
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix));
  if (!row) {
    return '';
  }
  return decodeURIComponent(row.slice(prefix.length));
};

const removeCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
};

const storage = {
  getAuthorization: () => {
    return localStorage.getItem(Authorization);
  },
  getToken: () => {
    return localStorage.getItem(Token);
  },
  getUserInfo: () => {
    return localStorage.getItem(UserInfo);
  },
  getUserInfoObject: () => {
    const userInfoStr = localStorage.getItem(UserInfo);
    return userInfoStr ? JSON.parse(userInfoStr) : null;
  },
  setAuthorization: (value: string) => {
    localStorage.setItem(Authorization, value);
  },
  setToken: (value: string) => {
    localStorage.setItem(Token, value);
  },
  setUserInfo: (value: string | Record<string, unknown>) => {
    const valueStr = typeof value !== 'string' ? JSON.stringify(value) : value;
    localStorage.setItem(UserInfo, valueStr);
  },
  setItems: (pairs: Record<string, string>) => {
    Object.entries(pairs).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  },
  setAuthBootstrapPending: (value: boolean) => {
    if (value) {
      sessionStorage.setItem(AUTH_BOOTSTRAP_PENDING_KEY, 'true');
    } else {
      sessionStorage.removeItem(AUTH_BOOTSTRAP_PENDING_KEY);
    }
  },
  isAuthBootstrapPending: () => {
    return sessionStorage.getItem(AUTH_BOOTSTRAP_PENDING_KEY) === 'true';
  },
  setUserStatusCache: (value: Record<string, unknown>) => {
    setCookie(USER_STATUS_CACHE_COOKIE, JSON.stringify(value), 1);
  },
  getUserStatusCache: () => {
    const raw = getCookie(USER_STATUS_CACHE_COOKIE);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },
  clearUserStatusCache: () => {
    removeCookie(USER_STATUS_CACHE_COOKIE);
    window.dispatchEvent(new Event(USER_STATUS_INVALIDATED_EVENT));
  },
  getUserStatusInvalidatedEventName: () => USER_STATUS_INVALIDATED_EVENT,
  removeAuthorization: () => {
    localStorage.removeItem(Authorization);
  },
  removeAll: () => {
    KeySet.forEach((x) => {
      localStorage.removeItem(x);
    });
    removeCookie(USER_STATUS_CACHE_COOKIE);
    sessionStorage.removeItem(AUTH_BOOTSTRAP_PENDING_KEY);
  },
  setLanguage: (lng: string) => {
    localStorage.setItem('lng', lng);
  },
  getLanguage: (): string => {
    return localStorage.getItem('lng') as string;
  },
};

export const getAuthorization = () => {
  const auth = getSearchValue('auth');
  const authorization = auth ? auth : storage.getAuthorization() || '';

  return authorization;
};

export default storage;

// Will not jump to the login page
export function redirectToLogin() {
  // const env = import.meta.env;
  window.location.href = location.origin + `/login`;
}
